import { DASHBOARD_PATH, NOT_FOUND_PATH } from '@helpers/constants';
import type { BaseRouteDefinition } from '@helpers/routeDefinitions';
// eslint-disable-next-line import/no-cycle
import useTryNavigate from '@hooks/useTryNavigate';
import useUserPermission from '@hooks/useUserPermission/useUserPermission';
import AccessDenied from '@pages/public/access-denied';
import { useRef, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

interface Props {
    definition: BaseRouteDefinition;
    depth: number;
    parentPath: string;
}

export default function ProtectedRoute({
    definition,
    depth,
    parentPath,
}: Props) {
    const [, setTryingToRedirect] = useState(false);
    const {
        isAllowed,
        isAllowedInFeatureToggle,
        userFeatureIsAllowed,
        modularProductIsAllowed,
        loading,
    } = useUserPermission();
    const { tryNavigateAsync } = useTryNavigate();
    const tryingToRedirectRef = useRef(false);

    const [shouldShow, loadingGuard] = definition.useGuard?.() ?? [true, false];

    const checkUserPermission =
        !!definition.profilePermission || !!definition.featurePermission;

    async function navigateToDashboard() {
        try {
            if (tryingToRedirectRef.current) return;
            tryingToRedirectRef.current = true;

            setTryingToRedirect(true);
            await tryNavigateAsync(DASHBOARD_PATH);
        } finally {
            tryingToRedirectRef.current = false;
            setTryingToRedirect(false);
        }
    }

    if (loading || loadingGuard || tryingToRedirectRef.current) return null;

    if (
        !shouldShow ||
        !isAllowedInFeatureToggle(definition.path) ||
        (parentPath && !isAllowedInFeatureToggle(parentPath))
    ) {
        void navigateToDashboard();
        return null;
    }

    if (!userFeatureIsAllowed(definition.featurePermission)) {
        return <AccessDenied />;
    }

    if (!modularProductIsAllowed(definition.modularProductPermission)) {
        return <AccessDenied modularProductPermission />;
    }

    if (
        checkUserPermission &&
        !isAllowed(
            definition.path,
            definition.profilePermission,
            definition.featurePermission,
            definition.modularProductPermission
        )
    ) {
        return <Navigate replace to={NOT_FOUND_PATH} />;
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{definition.element ?? <Outlet />}</>;
}
